/**
 * Auth Context - Manages authentication state across the app
 * Follows "Joyful Professional" design principles
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authApi from '../services/authApi';
import tokenManager from '../services/tokenManager';
import api from '../services/api';

const AuthContext = createContext(null);

// Distinguishes "assignments prefetch was attempted and failed" from "not
// attempted" (the default null) so a failure doesn't trigger an immediate
// identical retry — it should degrade straight to an empty list instead.
const ASSIGNMENTS_PREFETCH_FAILED = Symbol('assignments-prefetch-failed');

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);

  // New: Assignments and permissions
  const [assignments, setAssignments] = useState([]);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [academicYear, setAcademicYear] = useState(''); // Default to all years to ensure assignments load

  // Check online/offline status
  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = tokenManager.getAccessToken();

        if (!token) {
          setIsLoading(false);
          return;
        }

        // Check if token is expired
        if (tokenManager.isTokenExpired()) {
          // Try to refresh
          await refreshToken();
        } else {
          // Get current user + assignments concurrently — assignments are
          // scoped by token alone, so there's no need to wait for the user
          // call before starting it. A failed assignments fetch resolves to
          // a sentinel here (instead of rejecting) so it can't fail auth
          // outright; loadAssignmentsWithUser degrades it to an empty list
          // without retrying the same failing request a second time.
          try {
            const [userData, prefetchedAssignments] = await Promise.all([
              authApi.getCurrentUser(token),
              authApi.getUserAssignments(token, academicYear).catch((err) => {
                console.error('[AuthContext] Prefetch assignments failed:', err);
                return ASSIGNMENTS_PREFETCH_FAILED;
              }),
            ]);
            // Enrich user with name from teacher/officer records
            const enrichedUser = await enrichUserWithName(userData, token);
            setUser(enrichedUser);
            setIsAuthenticated(true);

            // Load assignments (user is already set at this point)
            await loadAssignmentsWithUser(token, enrichedUser, prefetchedAssignments);
          } catch (error) {
            // Token might be invalid, clear it
            tokenManager.clearTokens();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        tokenManager.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []); // eslint-disable-line

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRefresh = async () => {
      // Check every 5 minutes
      const interval = setInterval(async () => {
        if (tokenManager.isTokenExpired(300)) { // 5 minutes buffer
          try {
            await refreshToken();
          } catch (error) {
            console.error('Auto-refresh failed:', error);
            // If refresh fails, user will need to log in again
            logout();
          }
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    };

    const cleanup = checkAndRefresh();
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      }
    };
  }, [isAuthenticated]);

  /**
   * Map permission_level to resource permissions
   * This derives resource-level permissions from the permission_level field
   * @param {Object} assignment - Assignment object with permission_level field
   * @param {Object} user - User object (optional, for fallback)
   * @returns {Object} Resource permissions object { Progress: 'Edit', Attendance: 'Edit', ... }
   */
  const getDefaultPermissions = useCallback((assignment, user) => {
    // Use permission_level (new three-level system)
    // Backend returns permission_level: 'view', 'edit', or 'manage'
    if (assignment.permission_level) {
      const level = assignment.permission_level.toLowerCase();
      if (level === 'manage') {
        return {
          Progress: 'Manage',
          Attendance: 'Manage',
          Reports: 'Manage'
        };
      } else if (level === 'edit') {
        return {
          Progress: 'Edit',
          Attendance: 'Edit',
          Reports: 'View'
        };
      } else if (level === 'view') {
        return {
          Progress: 'View',
          Attendance: 'View',
          Reports: 'View'
        };
      }
    }

    // Fallback: If permission_level is missing, use user type
    // This should rarely happen if backend is working correctly
    if (user?.user_type === 'teacher') {
      return {
        Progress: 'Edit',
        Attendance: 'Edit',
        Reports: 'View'
      };
    }

    // Final fallback: Minimal view access (safest default)
    return {
      Progress: 'View',
      Attendance: 'View',
      Reports: 'View'
    };
  }, []);

  /**
   * Load user assignments and permissions
   * @param {string} token - Access token
   * @param {Object} userData - Optional user data (if available, for setting permissions)
   * @returns {Promise<void>}
   */
  const loadAssignmentsWithUser = useCallback(async (token, userData = null, prefetchedAssignments = null) => {
    try {
      console.log('[AuthContext] Loading assignments for user:', userData?.user_type, userData?.user_id, 'academic year:', academicYear);
      const userAssignments = prefetchedAssignments === ASSIGNMENTS_PREFETCH_FAILED
        ? []
        : (prefetchedAssignments ?? await authApi.getUserAssignments(token, academicYear));
      console.log('[AuthContext] Assignments loaded:', userAssignments?.length || 0, 'assignments');

      if (userAssignments && userAssignments.length > 0) {
        console.log('[AuthContext] Sample assignment:', userAssignments[0]);
      } else {
        console.warn('[AuthContext] No assignments found for user');
      }

      setAssignments(userAssignments || []);

      // Set active assignment (first one by default, or restore from localStorage)
      const savedActiveId = localStorage.getItem('activeAssignmentId');
      let active = null;

      if (savedActiveId) {
        active = userAssignments.find(a => a.id === parseInt(savedActiveId));
      }

      // Default to NULL (All Classes) if no saved preference, or if explicitly set to 'null'
      // Only set to first assignment if we want to force a specific view (User requested 'All' as default)
      if (!active && savedActiveId !== 'all') {
        // We essentially do nothing here, letting active be null
        // But let's check if we previously saved 'all'
      }

      // If we found a specific active assignment, set it
      if (active) {
        setActiveAssignment(active);
        const currentUser = userData || user;
        const permissions = active.permissions || getDefaultPermissions(active, currentUser);
        setPermissions(permissions);
        localStorage.setItem('activeAssignmentId', active.id.toString());
      } else {
        // Otherwise, explicit "All Classes" (null)
        setActiveAssignment(null);
        setPermissions(null); // Global view usually has no specific write perms, or we calculate them per card
        localStorage.setItem('activeAssignmentId', 'all');
      }
    } catch (error) {
      console.error('[AuthContext] Error loading assignments:', error);
      console.error('[AuthContext] Error details:', error.message, error.stack);
      setAssignments([]);
      setActiveAssignment(null);
      setPermissions(null);
    }
  }, [academicYear, getDefaultPermissions, user]);

  /**
   * Load user assignments and permissions (public wrapper)
   * @param {string} token - Access token
   * @returns {Promise<void>}
   */
  const loadAssignments = useCallback(async (token) => {
    return loadAssignmentsWithUser(token, null);
  }, [loadAssignmentsWithUser]);

  /**
   * Switch active assignment/division
   * @param {Object} assignment - Assignment object to activate
   */
  const switchAssignment = useCallback((assignment) => {
    setActiveAssignment(assignment);
    // Use permissions from backend if available, otherwise use defaults
    const permissions = assignment.permissions || getDefaultPermissions(assignment, user);
    setPermissions(permissions);
    localStorage.setItem('activeAssignmentId', assignment.id.toString());
  }, [getDefaultPermissions, user]);

  /**
   * Check if user has permission for a resource and action
   * @param {string} resource - Resource name (e.g., 'Progress', 'Attendance')
   * @param {string} level - Access level ('View', 'Edit', 'Manage')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = useCallback((resource, level) => {
    // LENIENT CHECK: If user has any assignments, they should be able to view
    // This is the most permissive approach - if they have assignments, they can access them
    if (assignments && assignments.length > 0) {
      // If they're just asking for View permission, always grant it if they have assignments
      if (level === 'View') {
        return true;
      }

      // For Edit/Manage, check permissions if available, otherwise use defaults
      // But still be lenient - if they have assignments, assume they can edit
      if (level === 'Edit' || level === 'Manage') {
        // If we have an active assignment and permissions, check them
        if (activeAssignment && permissions) {
          const resourcePermission = permissions[resource];
          if (resourcePermission) {
            const levels = { View: 1, Edit: 2, Manage: 3 };
            const requiredLevel = levels[level] || 0;
            const userLevel = levels[resourcePermission] || 0;
            return userLevel >= requiredLevel;
          }
        }

        // Fallback: if they have assignments, assume they can edit
        // This is lenient but reasonable - if they're assigned, they can edit
        if (activeAssignment) {
          const defaultPerms = getDefaultPermissions(activeAssignment, user);
          const resourcePermission = defaultPerms[resource];
          if (resourcePermission) {
            const levels = { View: 1, Edit: 2, Manage: 3 };
            const requiredLevel = levels[level] || 0;
            const userLevel = levels[resourcePermission] || 0;
            return userLevel >= requiredLevel;
          }
        }

        // Final fallback: if they have assignments, allow Edit (but not Manage)
        return level === 'Edit';
      }
    }

    // If no assignments at all, deny access
    return false;
  }, [permissions, activeAssignment, assignments, user, getDefaultPermissions]);

  /**
   * Get all divisions for current user (across all assignments)
   * @returns {Array} Array of division objects
   */
  const getAllDivisions = useCallback(() => {
    return assignments.map(a => ({
      id: a.division_id,
      name: a.division?.name || '',
      class: a.class?.name || '',
      school: a.school?.name || '',
      assignmentId: a.id,
      permissionLevel: a.permission_level,
      isActive: a.id === activeAssignment?.id
    }));
  }, [assignments, activeAssignment]);

  /**
   * Enrich user object with name from teacher/officer records
   * @param {Object} userData - User object from auth API
   * @param {string} token - Authentication token (optional, for API calls)
   * @returns {Promise<Object>} Enriched user object with name
   */
  const enrichUserWithName = useCallback(async (userData, token = null) => {
    if (!userData) {
      return userData;
    }

    // Check if name is already available in nested objects (backend returns user.teacher.name or user.officer.name)
    const name = userData.name || userData.teacher?.name || userData.officer?.name || userData.principal?.name;
    if (name) {
      // Flatten name to top level for easier access
      return { ...userData, name };
    }

    // If no token provided, try to get it from tokenManager
    const authToken = token || tokenManager.getAccessToken();
    if (!authToken) {
      return userData;
    }

    // Try to get user details from /me endpoint (only if name not already available)
    // This endpoint returns the current authenticated user with their name in officer/teacher object
    if ((userData.user_type === 'teacher' || userData.user_type === 'officer') && userData.user_id) {
      try {
        const API_BASE = process.env.REACT_APP_RESOURCE_API || 'https://neev-api.onrender.com';
        const response = await fetch(`${API_BASE}/api/v1/user-auth/me`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Backend returns { user: { user_type, user_id, officer: { name, ... }, teacher: { name, ... } } }
          if (data.user) {
            const fetchedName = data.user.officer?.name || data.user.teacher?.name || data.user.name;
            if (fetchedName) {
              return { ...userData, name: fetchedName };
            }
          }
        }
      } catch (e) {
        // Endpoint not available or failed - that's okay, name enrichment is optional
        // User can still function without it - don't log errors to avoid noise
      }
    }

    return userData;
  }, []);

  /**
   * Refresh assignments (useful after admin makes changes)
   */
  const refreshAssignments = useCallback(async () => {
    const token = tokenManager.getAccessToken();
    if (token) {
      await loadAssignments(token);
    }
  }, [loadAssignments]);

  /**
   * Login user
   * @param {string} phone - User phone number
   * @param {string} password - User password
   * @returns {Promise<void>}
   */
  const login = async (phone, password) => {
    try {
      setIsLoading(true);
      const { token, refreshToken, expiresAt, user: userData } = await authApi.login(phone, password);

      // Store tokens in tokenManager (System 1)
      tokenManager.setTokens(token, refreshToken, expiresAt);

      // Enrich user with name from teacher/officer records
      const enrichedUser = await enrichUserWithName(userData, token);

      // Update state
      setUser(enrichedUser);
      setIsAuthenticated(true);

      // Login response already embeds the full assignments list (backend enriches
      // it specifically for this response) - reuse it instead of firing a separate
      // /assignments call right after login. Same prefetch pattern initializeAuth
      // already uses for session restore.
      await loadAssignmentsWithUser(token, enrichedUser, userData.assignments ?? null);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      const token = tokenManager.getAccessToken();
      if (token) {
        await authApi.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call success
      tokenManager.clearTokens();

      setUser(null);
      setIsAuthenticated(false);
      setAssignments([]);
      setActiveAssignment(null);
      setPermissions(null);
      localStorage.removeItem('activeAssignmentId');
    }
  };

  /**
   * Refresh access token
   * @returns {Promise<void>}
   */
  const refreshToken = useCallback(async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes

    try {
      setIsRefreshing(true);
      const refreshTokenValue = tokenManager.getRefreshToken();

      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const { token, refreshToken: newRefreshToken, expiresAt, user: userData } =
        await authApi.refreshToken(refreshTokenValue);

      // Update tokens
      tokenManager.setTokens(token, newRefreshToken, expiresAt);

      // Enrich user with name from teacher/officer records
      const enrichedUser = await enrichUserWithName(userData, token);

      // Update user data
      setUser(enrichedUser);
      setIsAuthenticated(true);
    } catch (error) {
      // Refresh failed - clear tokens and logout
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  /**
   * Switch active role
   * @param {string} roleCode - Role code to switch to
   * @returns {Promise<void>}
   */
  const switchRole = async (roleCode) => {
    try {
      setIsLoading(true);
      const token = tokenManager.getAccessToken();
      const { token: newToken, expiresAt, user: userData } =
        await authApi.switchRole(token, roleCode);

      // Update tokens
      const refreshTokenValue = tokenManager.getRefreshToken();
      tokenManager.setTokens(newToken, refreshTokenValue, expiresAt);

      // Enrich user with name from teacher/officer records
      const enrichedUser = await enrichUserWithName(userData, newToken);

      // Update user data
      setUser(enrichedUser);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get access token (for API calls)
   * @returns {string|null} Access token
   */
  // Stable identity: data effects across the app list getToken as a dependency, so a new
  // function per render would refetch everything on every AuthProvider re-render
  // (e.g. each online/offline flip). Reads the current token at call time.
  const getToken = useCallback(() => tokenManager.getAccessToken(), []);

  const value = {
    // User state
    user,
    isAuthenticated,
    isLoading,
    isRefreshing,
    offline,

    // Assignments and permissions
    assignments,
    activeAssignment,
    permissions,
    academicYear,

    // Auth functions
    login,
    logout,
    refreshToken,
    switchRole,
    getToken,

    // Assignment functions
    switchAssignment,
    hasPermission,
    getAllDivisions,
    refreshAssignments,
    setAcademicYear,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

